import React, { useState } from 'react';
import { HashRouter, BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import Home from './components/Home';
import Verify from './components/Auth/Verify';
import StudyGuides from './components/StudyGuides';
import FlashCards from './components/FlashCards';
import Videos from './components/Videos';
import Pricing from './components/Pricing';
import About from './components/About';
import Terms from './components/Terms';
import FAQ from './components/FAQ';
import Privacy from './components/Privacy';
import Profile from './components/profile';
import { QuestionBank } from './components/QuestionBank';
import { SignIn, SignUp, useAuth, AuthProvider, ProtectedRoute } from './components/Auth';
import ForgotPassword from './components/Auth/ForgotPassword';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import { AcademyLanding, SubjectDetail, StudyGuidesView, FlashcardsView, VideosView, Progress } from './components/Academy';

const isMobileApp = process.env.REACT_APP_PLATFORM === 'ios' || process.env.REACT_APP_PLATFORM === 'android';

// Mobile builds load index.html from the device's local filesystem, where
// path-based routing can't work — hash routing keeps everything on one
// static URL. The web build uses BrowserRouter so routes are real,
// crawlable URLs (CloudFront is configured to fall back to /index.html).
const Router = isMobileApp ? HashRouter : BrowserRouter;

function App() {
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [timeTaken, setTimeTaken] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const endQuiz = (finalScore, finalTime, quizQuestions, answers) => {
    setScore(finalScore);
    setTimeTaken(finalTime);
    setQuestions(quizQuestions);
    setUserAnswers(answers);
    setQuizCompleted(true);
  };

  const returnToHome = () => {
    setQuizCompleted(false);
    setScore(0);
    setTimeTaken(0);
    setQuestions([]);
    setUserAnswers({});
  };

  return (
    <AuthProvider>
      <Router>
        <AppContent
          quizCompleted={quizCompleted}
          score={score}
          timeTaken={timeTaken}
          questions={questions}
          userAnswers={userAnswers}
          endQuiz={endQuiz}
          returnToHome={returnToHome}
        />
      </Router>
    </AuthProvider>
  );
}

function AppContent({ quizCompleted, score, timeTaken, questions, userAnswers, endQuiz, returnToHome }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isSubscribed } = useAuth();

  return (
    <div className="App font-quicksand flex flex-col min-h-screen">
      <nav className="bg-gradient-to-r from-study-pink to-study-teal py-5 px-5 md:px-10 shadow-md relative">
        <div className="flex justify-between items-center">
          <button
            className="md:hidden text-study-black text-2xl focus:outline-none"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </button>
          <div className="hidden md:flex md:space-x-10">
            <Link to="/" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all">
              Home
            </Link>
            <div className="relative">
              <span
                className="text-study-black text-lg font-bold hover:text-study-yellow transition-all cursor-pointer"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
              >
                StudyPass
              </span>
              {isDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 w-48 bg-study-teal rounded-xl shadow-lg py-2 z-10">
                  <Link
                    to="/quiz-bank"
                    className="block px-4 py-2 text-study-black hover:text-study-yellow hover:bg-study-pink transition-all rounded-t-xl"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    Quiz Bank
                  </Link>
                  <Link
                    to="/academy"
                    className="block px-4 py-2 text-study-black hover:text-study-yellow hover:bg-study-pink transition-all rounded-b-xl"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    Essentials
                  </Link>
                </div>
              )}
            </div>
            {!isSubscribed && !isMobileApp && (
              <Link to="/pricing" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all">
                Pricing
              </Link>
            )}
            <Link to="/about" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all">
              About
            </Link>
          </div>
          <div className="absolute top-5 right-5 md:right-10">
            <UserMenu
              setIsDropdownOpen={setIsDropdownOpen}
              isUserMenuOpen={isUserMenuOpen}
              setIsUserMenuOpen={setIsUserMenuOpen}
            />
          </div>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 flex flex-col space-y-4">
            <Link to="/" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all" onClick={() => setIsMobileMenuOpen(false)}>
              Home
            </Link>
            <div className="relative">
              <span
                className="text-study-black text-lg font-bold hover:text-study-yellow transition-all cursor-pointer"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
              >
                StudyPass
              </span>
              {isDropdownOpen && (
                <div className="mt-2 w-48 bg-study-teal rounded-xl shadow-lg py-2">
                  <Link
                    to="/quiz-bank"
                    className="block px-4 py-2 text-study-black hover:text-study-yellow hover:bg-study-pink transition-all rounded-t-xl"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Quiz Bank
                  </Link>
                  <Link
                    to="/academy"
                    className="block px-4 py-2 text-study-black hover:text-study-yellow hover:bg-study-pink transition-all rounded-b-xl"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Essentials
                  </Link>
                </div>
              )}
            </div>
            {!isSubscribed && (
              <Link to="/pricing" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all" onClick={() => setIsMobileMenuOpen(false)}>
                Pricing
              </Link>
            )}
            <Link to="/about" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all" onClick={() => setIsMobileMenuOpen(false)}>
              About
            </Link>
          </div>
        )}
      </nav>
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/SignIn" element={<SignIn />} />
          <Route path="/SignUp" element={<SignUp />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/dashboard"
            element={<Navigate to="/" replace />}
          />
          <Route path="/subscription-success" element={<SubscriptionSuccess />} />
          <Route
            path="/quiz-bank"
            element={
              <ProtectedRoute>
                <QuestionBank
                  quizCompleted={quizCompleted}
                  score={score}
                  timeTaken={timeTaken}
                  questions={questions}
                  userAnswers={userAnswers}
                  onQuizEnd={endQuiz}
                  onReturnToHome={returnToHome}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/study-guides"
            element={
              <ProtectedRoute>
                <StudyGuides />
              </ProtectedRoute>
            }
          />
          <Route
            path="/flash-cards"
            element={
              <ProtectedRoute>
                <FlashCards />
              </ProtectedRoute>
            }
          />
          <Route
            path="/videos"
            element={
              <ProtectedRoute>
                <Videos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/academy"
            element={
              <ProtectedRoute>
                <AcademyLanding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/academy/:subjectId"
            element={
              <ProtectedRoute>
                <SubjectDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/academy/:subjectId/study-guides"
            element={
              <ProtectedRoute>
                <StudyGuidesView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/academy/:subjectId/flashcards"
            element={
              <ProtectedRoute>
                <FlashcardsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/academy/:subjectId/videos"
            element={
              <ProtectedRoute>
                <VideosView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/progress"
            element={
              <ProtectedRoute>
                <Progress />
              </ProtectedRoute>
            }
          />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      <footer className="bg-gradient-to-r from-study-teal to-study-pink py-5 flex justify-center items-center space-x-10 shadow-md">
        <Link to="/faq" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all">FAQ</Link>
        <Link to="/terms" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all">Terms</Link>
        <Link to="/privacy" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all">Privacy Policy</Link>
      </footer>
    </div>
  );
}

function UserMenu({ setIsDropdownOpen, isUserMenuOpen, setIsUserMenuOpen }) {
  const { isAuthenticated, userEmail, signOut } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
      setIsDropdownOpen(false);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="relative">
      {isAuthenticated ? (
        <div>
          <button
            className="text-study-black text-lg font-bold hover:text-study-yellow transition-all"
            onClick={() => setIsUserMenuOpen((prev) => !prev)}
          >
            {userEmail ? userEmail.split('@')[0] : 'User'}
          </button>
          {isUserMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-32 bg-study-teal rounded-xl shadow-lg py-2 z-10">
              <Link
                to="/profile"
                className="block w-full text-left px-4 py-2 text-study-black hover:text-study-yellow hover:bg-study-pink transition-all rounded-xl"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Profile
              </Link>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 text-study-black hover:text-study-yellow hover:bg-study-pink transition-all rounded-xl"
              >
                Log Out
              </button>
            </div>
          )}
        </div>
      ) : (
        <Link to="/SignIn" className="text-study-black text-lg font-bold hover:text-study-yellow transition-all">
          Sign In
        </Link>
      )}
    </div>
  );
}

export default App;